import { css } from '@emotion/react';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import {
  ExportReportSummaryComponent, type ExportReportSummaryProps,
} from '~/exportReport/summary/exportReportSummary.component';
import { ExportReportTableComponent } from '~/exportReport/table/exportReportTable.component';
import { ExportReportTableDataRowComponent } from '~/exportReport/table/exportReportTableDataRow.component';
import { ExportReportTableHeaderComponent } from '~/exportReport/table/exportReportTableHeader.component';
import { ExportReportTableTitleRowComponent } from '~/exportReport/table/exportReportTableTitleRow.component';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';

export type ReportTableColumn = {
  id: string;
  heading: string;
  secondaryHeading: string;
};

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  tableColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportSummaryProps;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'css'>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicValue: (columnId: string, demographicId: string) => string | null;
}>;

const tableStyle = css({
  marginTop: 10,
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

export const ExportReportTemplateRendererComponent = ({ template, summaryProps, onMapLoaded, mapProps, tableColumns, getDemographicLabel, getDemographicValue }: ExportReportTemplateRendererProps) => {
  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} />

      {template.items.map((templateItem, index) => {

        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${index}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${index}_roadmap`}
                  mapType="roadmap"
                  {...mapProps}
                />

                <ExportReportMapComponent
                  id={`map${index}_hybrid`}
                  mapType="hybrid"
                  {...mapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${index}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${index}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${index}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            return (
              <ExportReportTableComponent key={`table-${index}`} css={tableStyle}>
                <ExportReportTableHeaderComponent
                  label={templateItem.title}
                  columnNames={tableColumns}
                />

                <tbody>
                  {templateItem.content.map((templateRow, index) => {
                    if (templateRow.type === 'data-row') {
                      const values = tableColumns.map(column => getDemographicValue(column.id, templateRow.id) ?? '');
                      const label = templateRow.custom_label ?? getDemographicLabel(templateRow.id) ?? '';

                      return (
                        <ExportReportTableDataRowComponent
                          key={`data-row${index}`}
                          label={label}
                          values={values}
                        />
                      );
                    }

                    if (templateRow.type === 'title-row') {
                      return (
                        <ExportReportTableTitleRowComponent
                          key={`title-row-${index}`}
                          title={templateRow.content}
                        />
                      );
                    }

                    return null;
                  })}
                </tbody>
              </ExportReportTableComponent>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
